import {
  AdminDeleteOnboardingTemplate,
  AdminFetchOnboardingTemplateList,
  AdminRenameOnboardingTemplate,
} from 'src/constants/actionTypes'
import type { DeleteOnboardingTemplatePayload } from 'src/interfaces/onboardingTemplates'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { OnboardingTemplateListState } from './types'

const getInitialState = (): OnboardingTemplateListState => {
  return {
    templates: [],
    templatesLoading: false,
    templatesError: null,
    saving: false,
    deletingId: null,
  }
}

export const onboardingTemplateListReducer = (
  state: OnboardingTemplateListState = getInitialState(),
  action: Action<unknown>,
): OnboardingTemplateListState => {
  switch (action.type) {
    case AdminFetchOnboardingTemplateList.type.REQUEST:
      return {
        ...state,
        // Cleared so rows from the previously viewed customer never flash in the grid.
        templates: [],
        templatesLoading: true,
        templatesError: null,
      }
    case AdminFetchOnboardingTemplateList.type.SUCCESS: {
      const templates = (action.payload as OnboardingTemplate[] | undefined) ?? []

      return {
        ...state,
        templates,
        templatesLoading: false,
        templatesError: null,
      }
    }
    case AdminFetchOnboardingTemplateList.type.FAILED:
      return {
        ...state,
        templatesLoading: false,
        templatesError: resolveErrorMessage(action.payload, 'Failed to load onboarding templates'),
      }

    // Rename never writes to `templates` — the list is refetched on success, which is the
    // single source of truth. See the design doc's §7.
    case AdminRenameOnboardingTemplate.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminRenameOnboardingTemplate.type.SUCCESS:
    case AdminRenameOnboardingTemplate.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    // Like rename, delete never writes to `templates` — the page refetches the list on success.
    case AdminDeleteOnboardingTemplate.type.REQUEST: {
      const payload = action.payload as Partial<DeleteOnboardingTemplatePayload> | undefined

      return {
        ...state,
        deletingId: payload?.collectDataTemplateId ?? null,
      }
    }
    case AdminDeleteOnboardingTemplate.type.SUCCESS:
    case AdminDeleteOnboardingTemplate.type.FAILED:
      return {
        ...state,
        deletingId: null,
      }

    default:
      return state
  }
}
